export type LearningObjectiveNode = { guid: string; parent_guid?: string | null };

function groupBy<T>(list: T[], getKey: (item: T) => string | null | undefined): Map<string, T[]> {
  const groups = new Map<string, T[]>();
  list.forEach((item) => {
    const key = getKey(item);
    if (key == null) return;
    const group = groups.get(key);
    if (group) group.push(item);
    else groups.set(key, [item]);
  });
  return groups;
}

// Recorre por niveles cada rama y apunta los guids ya expandidos: la versión anterior devolvía la
// lista entera por cada elemento y, con la búsqueda vacía, el array crecía hasta «Invalid array
// length» (TANGERINE-FRONTOFFICE-566); y un ciclo en `parent_guid` la dejaba en recursión infinita.
function collectRelatives<T extends LearningObjectiveNode>(start: T[], getNext: (item: T) => T[], seen: Set<string>, result: T[]): void {
  const expanded = new Set<string>();

  function visit(level: T[]): void {
    level.forEach((item) => {
      if (seen.has(item.guid)) return;
      seen.add(item.guid);
      result.push(item);
    });
    level.forEach((item) => {
      if (expanded.has(item.guid)) return;
      expanded.add(item.guid);
      const next = getNext(item);
      if (next.length > 0) visit(next);
    });
  }

  visit(start);
}

/** Los objetivos que coinciden con la búsqueda, sus ascendientes y sus descendientes, sin repetir. */
export function getObjectivesWithRelatives<T extends LearningObjectiveNode>(all: T[], matches: T[]): T[] {
  const byGuid = groupBy(all, (item) => item.guid);
  const byParentGuid = groupBy(all, (item) => item.parent_guid);
  const seen = new Set<string>();
  const result: T[] = [];

  collectRelatives(matches, (item) => (item.parent_guid == null ? [] : (byGuid.get(item.parent_guid) ?? [])), seen, result);
  collectRelatives(matches, (item) => byParentGuid.get(item.guid) ?? [], seen, result);
  return result;
}
